import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle2, ArrowRight, Loader2, Sparkles, X } from 'lucide-react';
import BrandLogo from '../components/BrandLogo';
import SiteFooter from '../components/SiteFooter';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function SubscribeSuccessPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get('session_id');
  const [status, setStatus] = useState({ loading: true, paid: false, plan: null });

  useEffect(() => {
    if (!sessionId) { setStatus({ loading: false, paid: false, plan: null, error: 'Missing session id' }); return; }
    let attempts = 0;
    let cancelled = false;
    const poll = async () => {
      while (!cancelled && attempts < 8) {
        try {
          const r = await fetch(`${API}/subscriptions/status/${sessionId}`);
          if (r.ok) {
            const d = await r.json();
            if (d.payment_status === 'paid') {
              setStatus({ loading: false, paid: true, plan: d.plan_name || d.plan_id });
              return;
            }
          }
        } catch { /* noop */ }
        attempts += 1;
        await new Promise((r) => setTimeout(r, 1500));
      }
      if (!cancelled) setStatus({ loading: false, paid: false, plan: null, error: 'Still processing…' });
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-[#030303] text-white relative overflow-hidden">
      <div
        aria-hidden
        className="absolute -top-40 left-1/2 -translate-x-1/2 w-[720px] h-[720px] rounded-full blur-3xl opacity-30 pointer-events-none"
        style={{ background: 'radial-gradient(circle, #00F0FF33 0%, transparent 70%)' }}
      />
      <div
        aria-hidden
        className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[720px] h-[720px] rounded-full blur-3xl opacity-20 pointer-events-none"
        style={{ background: 'radial-gradient(circle, #FF007F33 0%, transparent 70%)' }}
      />

      <header className="relative z-10 border-b border-white/[0.06] bg-black/40 backdrop-blur-xl">
        <div className="mx-auto max-w-7xl px-6 py-4 flex items-center justify-between">
          <button onClick={() => navigate('/')} data-testid="subscribe-success-home-btn">
            <BrandLogo size={30} />
          </button>
        </div>
      </header>

      <main className="relative z-10 mx-auto max-w-2xl px-6 pt-20 pb-24 text-center">
        {status.loading ? (
          <div className="py-16">
            <Loader2 size={40} className="mx-auto text-white/40 animate-spin" />
            <div className="mt-6 text-white/70 text-[15px]">Confirming your payment…</div>
          </div>
        ) : status.paid ? (
          <div data-testid="subscribe-success-paid">
            <div className="mx-auto h-20 w-20 rounded-full flex items-center justify-center"
              style={{ background: 'radial-gradient(circle, rgba(0,240,255,0.3), transparent 70%)' }}>
              <CheckCircle2 size={56} className="text-emerald-400" />
            </div>
            <h1 className="mt-8 text-4xl md:text-5xl font-display font-black tracking-[-0.03em]"
              style={{ background: 'linear-gradient(180deg,#fff,#94a3b8)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
              You're subscribed.
            </h1>
            {status.plan && (
              <div className="mt-4 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.04] px-4 py-1.5 text-[13px] text-white/80">
                <Sparkles size={13} className="text-[#00F0FF]" />
                Plan: <span className="font-semibold text-white">{status.plan}</span>
              </div>
            )}
            <p className="mt-6 text-white/70 leading-relaxed">
              Your monthly credits are now active. A receipt is on the way to your email.
            </p>
            <div className="mt-10 flex items-center justify-center gap-3 flex-wrap">
              <Link
                to="/build"
                data-testid="subscribe-success-open-builder"
                className="inline-flex items-center gap-1.5 rounded-full text-black font-semibold text-[14px] px-6 py-3 hover:scale-[1.02] transition"
                style={{ background: 'linear-gradient(90deg, #00F0FF 0%, #FF007F 100%)' }}
              >
                Open the Builder <ArrowRight size={13} />
              </Link>
              <Link
                to="/souls"
                data-testid="subscribe-success-open-souls"
                className="inline-flex items-center gap-1.5 rounded-full border border-white/15 bg-white/[0.04] hover:bg-white/[0.08] text-white text-[14px] font-medium px-6 py-3 transition"
              >
                Browse SOUL Library
              </Link>
            </div>
          </div>
        ) : (
          <div>
            <div className="mx-auto h-20 w-20 rounded-full flex items-center justify-center bg-rose-500/10 border border-rose-500/30">
              <X size={44} className="text-rose-400" />
            </div>
            <h1 className="mt-8 text-3xl md:text-4xl font-display font-black">Still processing</h1>
            <p className="mt-4 text-white/60 max-w-lg mx-auto">
              Stripe hasn't confirmed your payment yet. This can take up to a minute. If your card was charged, you'll receive a receipt by email — your plan will activate automatically.
            </p>
            <div className="mt-8 flex items-center justify-center gap-3">
              <Link to="/pricing" className="rounded-full border border-white/15 bg-white/[0.04] hover:bg-white/[0.08] text-white text-[14px] font-medium px-5 py-2.5">
                Back to Pricing
              </Link>
              <Link to="/contact" className="rounded-full text-black font-semibold text-[14px] px-5 py-2.5"
                style={{ background: 'linear-gradient(90deg, #00F0FF 0%, #FF007F 100%)' }}>
                Contact support
              </Link>
            </div>
          </div>
        )}
      </main>

      <SiteFooter />
    </div>
  );
}
